/**
 * Lista de usuarios — /configuracoes/acessos/usuarios.
 *
 * Renderiza:
 *  - Header com acao primaria "+ Novo usuario" e refresh
 *  - Filtros inline: busca, cargo, status
 *  - Tabela com avatar+nome, email, cargo, status, MFA, ultimo acesso
 *  - Linha clicavel -> ficha do usuario
 *
 * P7 (power user): filtros persistem em localStorage (chave proprias).
 * P10: empty states distintos para "sem filtro retornando 0" vs "0 cadastrados".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiRefreshCw } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";

import UserAvatar from "../../../components/UserAvatar";
import { useAuth } from "../../../context/AuthContext";

import { useRespostaMaisRecente, useValorComAtraso } from "../../../hooks/useBuscaRemota";
import { fetchCargos, fetchTodosUsuarios } from "../api";
import {
  ActionsBar,
  Card,
  CardHeader,
  CardTitle,
  EmptyMsg,
  ErrorMsg,
  Pill,
  PrimaryButton,
  SearchBar,
  SecondaryButton,
  Select,
  Table,
} from "../styles.jsx";
import { getItemTenant, setItemTenant } from "../../../utils/tenantStorage";

const STORAGE_FILTROS = "pharuerp:acessos:usuarios:filtros";

function lerFiltros(searchParams) {
  // Prioriza query string (deep-link). Cai para localStorage.
  const fromQs = {
    search: searchParams.get("search") || "",
    cargo: searchParams.get("cargo") || "",
    ativo: searchParams.get("ativo") || "",
  };
  if (Object.values(fromQs).some((v) => v)) return fromQs;
  try {
    return JSON.parse(getItemTenant(STORAGE_FILTROS) || "{}");
  } catch {
    return {};
  }
}

function salvarFiltros(filtros) {
  try {
    setItemTenant(STORAGE_FILTROS, JSON.stringify(filtros));
  } catch {
    // sem storage = sem persistencia.
  }
}

function badgeStatus(usuario) {
  if (!usuario.ativo) return { label: "Inativo", tone: "danger" };
  if (usuario.primeiro_acesso) return { label: "1º acesso", tone: "warning" };
  return { label: "Ativo", tone: "success" };
}

function UsuariosLista() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  const podeCriar = hasPermission("usuario:create");
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState(() => ({
    search: "",
    cargo: "",
    ativo: "",
    // Filtros client-side (Sprint 6 polimento):
    mfa: "",  // "" | "exigido" | "nao_exigido"
    primeiro_acesso: "",  // "" | "pendente" | "concluido"
    ...lerFiltros(searchParams),
  }));

  const [estado, setEstado] = useState({
    loading: true,
    usuarios: [],
    total: 0,
    error: null,
  });
  const [cargos, setCargos] = useState([]);

  // A busca vai ao servidor: espera a pessoa parar de digitar e descarta a
  // resposta de uma busca ja superada. Sem isso cada tecla disparava uma
  // requisicao e a lista mostrava a que chegasse por ultimo (digitar "giov"
  // podia terminar mostrando o resultado de "g", ou a lista inteira).
  const buscaAtrasada = useValorComAtraso(filtros.search);
  const iniciarCarga = useRespostaMaisRecente();
  const { cargo, ativo } = filtros;

  const carregar = useCallback(async () => {
    const vale = iniciarCarga();
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const payload = await fetchTodosUsuarios(request, { search: buscaAtrasada.trim(), cargo, ativo });
      if (!vale()) return;
      setEstado({
        loading: false,
        usuarios: payload?.data || [],
        total: payload?.total || 0,
        error: null,
      });
    } catch (err) {
      if (!vale()) return;
      setEstado({
        loading: false,
        usuarios: [],
        total: 0,
        error: err?.message || "Erro ao carregar usuários.",
      });
    }
  }, [request, buscaAtrasada, cargo, ativo, iniciarCarga]);

  // MFA e 1o acesso filtram no navegador: mudar um deles nao refaz a consulta.
  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    salvarFiltros(filtros);
  }, [filtros]);

  useEffect(() => {
    (async () => {
      try {
        const payload = await fetchCargos(request, false);
        setCargos(payload?.resultados || payload?.data || []);
      } catch {
        setCargos([]);
      }
    })();
  }, [request]);

  const aplicarFiltro = (chave, valor) => {
    const novo = { ...filtros, [chave]: valor };
    setFiltros(novo);
    // Sincroniza query string para deep-link e fallback de share.
    const usp = new URLSearchParams();
    Object.entries(novo).forEach(([k, v]) => {
      if (v) usp.set(k, v);
    });
    setSearchParams(usp, { replace: true });
  };

  // Aplicacao client-side dos filtros MFA / primeiro_acesso. Esses
  // criterios NAO existem no endpoint backend (ainda), entao filtramos
  // localmente apos receber a lista paginada.
  const linhas = useMemo(() => {
    let resultado = estado.usuarios;
    if (filtros.mfa === "exigido") {
      resultado = resultado.filter((u) => u.mfa_obrigatorio);
    } else if (filtros.mfa === "nao_exigido") {
      resultado = resultado.filter((u) => !u.mfa_obrigatorio);
    }
    if (filtros.primeiro_acesso === "pendente") {
      resultado = resultado.filter((u) => u.primeiro_acesso);
    } else if (filtros.primeiro_acesso === "concluido") {
      resultado = resultado.filter((u) => !u.primeiro_acesso);
    }
    return resultado;
  }, [estado.usuarios, filtros.mfa, filtros.primeiro_acesso]);

  const corpo = useMemo(() => {
    if (estado.loading) return <EmptyMsg>Carregando...</EmptyMsg>;
    if (!linhas.length) {
      const temFiltro = Boolean(
        filtros.search || filtros.cargo || filtros.ativo || filtros.mfa || filtros.primeiro_acesso,
      );
      return (
        <EmptyMsg>
          {temFiltro
            ? "Nenhum usuário com esses filtros."
            : "Nenhum usuário cadastrado ainda."}
        </EmptyMsg>
      );
    }
    return (
      <Table $rowsClickable>
        <thead>
          <tr>
            <th>Usuário</th>
            <th>Cargo</th>
            <th>Status</th>
            <th>MFA</th>
            <th>Preços</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((u) => {
            const status = badgeStatus(u);
            const cargoNome = u.cargo?.nome || u.cargo_nome || u.cargo_exibido || "—";
            return (
              <tr
                key={u.id}
                onClick={() => navigate(`/configuracoes/acessos/usuarios/${u.id}`)}
              >
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UserAvatar
                      src={u.foto_url}
                      nome={u.nome}
                      email={u.email}
                      size="sm"
                    />
                    <div>
                      <div style={{ fontWeight: 600 }}>{u.nome}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <Pill $tone="neutral">{cargoNome}</Pill>
                </td>
                <td>
                  <Pill $tone={status.tone}>{status.label}</Pill>
                </td>
                <td>
                  <Pill $tone={u.mfa_obrigatorio ? "info" : "neutral"}>
                    {u.mfa_obrigatorio ? "Exigido" : "—"}
                  </Pill>
                </td>
                <td>
                  <Pill $tone={u.pode_ver_precos ? "success" : "neutral"}>
                    {u.pode_ver_precos ? "Sim" : "Não"}
                  </Pill>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }, [
    estado.loading, filtros.ativo, filtros.cargo, filtros.search, filtros.mfa, filtros.primeiro_acesso,
    linhas, navigate,
  ]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Usuários
          {!estado.loading && estado.total > 0 && (
            <small style={{ opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>
              {linhas.length === estado.total
                ? `${estado.total} no total`
                : `${linhas.length} de ${estado.total}`}
            </small>
          )}
        </CardTitle>
        <ActionsBar>
          {podeCriar && (
            <PrimaryButton
              type="button"
              onClick={() => navigate("/configuracoes/acessos/usuarios/novo")}
              title="Criar usuário via wizard"
            >
              <FiPlus size={14} /> Novo usuário
            </PrimaryButton>
          )}
          <SecondaryButton type="button" onClick={carregar} disabled={estado.loading}>
            <FiRefreshCw size={14} /> Atualizar
          </SecondaryButton>
        </ActionsBar>
      </CardHeader>

      <ActionsBar>
        <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
          <SearchBar
            type="search"
            placeholder="Buscar por nome ou email..."
            value={filtros.search}
            onChange={(e) => aplicarFiltro("search", e.target.value)}
            aria-label="Buscar usuário"
          />
        </div>
        <Select
          value={filtros.cargo}
          onChange={(e) => aplicarFiltro("cargo", e.target.value)}
          aria-label="Filtrar por cargo"
        >
          <option value="">Todos os cargos</option>
          {cargos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </Select>
        <Select
          value={filtros.ativo}
          onChange={(e) => aplicarFiltro("ativo", e.target.value)}
          aria-label="Filtrar por status"
        >
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </Select>
        <Select
          value={filtros.mfa}
          onChange={(e) => aplicarFiltro("mfa", e.target.value)}
          aria-label="Filtrar por MFA"
        >
          <option value="">Todos (MFA)</option>
          <option value="exigido">MFA exigido</option>
          <option value="nao_exigido">MFA não exigido</option>
        </Select>
        <Select
          value={filtros.primeiro_acesso}
          onChange={(e) => aplicarFiltro("primeiro_acesso", e.target.value)}
          aria-label="Filtrar por primeiro acesso"
        >
          <option value="">Qualquer 1o acesso</option>
          <option value="pendente">1o acesso pendente</option>
          <option value="concluido">1º acesso concluído</option>
        </Select>
        {(filtros.search ||
          filtros.cargo ||
          filtros.ativo ||
          filtros.mfa ||
          filtros.primeiro_acesso) && (
          <SecondaryButton
            type="button"
            onClick={() => {
              setFiltros({
                search: "",
                cargo: "",
                ativo: "",
                mfa: "",
                primeiro_acesso: "",
              });
              setSearchParams({}, { replace: true });
            }}
          >
            Limpar filtros
          </SecondaryButton>
        )}
      </ActionsBar>

      {estado.error && <ErrorMsg>{estado.error}</ErrorMsg>}
      {corpo}
    </Card>
  );
}

export default UsuariosLista;
